import { ActualityHqWithCongestionsRow } from '@warebee/shared/dataset-queries';
import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { toHumanReadableTime } from '../../common/dateTimeHelper';
import useFormatter from '../../common/useFormatter';
import useGetNamedColor from '../../common/useGetNamedColor';
import ButtonLocateLocation from '../../components/ButtonLocateLocation';
import EventSection from '../../containers/EventSection';
import { actualityResourcePolicy } from '../../feed/store/actuality.state';
import { AgentCategoryId } from '../../resourcePolicy/agentData/agent.types';
import { getAgentIcon } from '../../resourcePolicy/agentData/agentDataHelper';
import EventListItemHeader from '../../simulation/panels/route/EventListItemHeader';
import { actualitySelectedEvent } from '../store/actuality.hq.state';
import { actualityVisibleDeviceIndicatorsSelector } from '../store/actuality.state';

import useSelectLocationById from '../../layout/hooks/useSelectLocationById';
import {
  viewerLayoutId,
  viewerSelectedLocationIdWithToggle,
} from '../../layout/viewer/store/viewer.state';
import { useActualityStatsCategories } from '../constants/actuality.categories';
// import { feedDisabledAgentTypes } from '../store/feed.state';
import { IssueMiniInline } from '../../simulation/panels/route/StatInline';
import { ActualityEntityTagProps } from './ActualityIEntityTag';
import ConfigurableEventIssues from './ConfigurableEventIssues';
import { EventProcessDetails } from './EventProcessDetails';
import IndicatorEnergy from './IndicatorEnergy';
import IndicatorSignalStrength from './IndicatorSignalStrength';
import StackedLineChart, { StackedLineMetricConfig } from './StackedLineChart';

export type FeedActivityEventDetailsProps = {
  index: number;
  event: ActualityHqWithCongestionsRow;
  onHover?: (event: ActualityHqWithCongestionsRow) => void;
  onClick?: (event: ActualityHqWithCongestionsRow) => void;
  grouping?: 'agent' | 'location' | 'item' | 'process' | 'time' | 'all';
  className?: string;
};

const FeedActivityEventDetails: React.FC<
  FeedActivityEventDetailsProps
> = props => {
  const { t } = useTranslation('feed');
  const formatter = useFormatter();
  // const disabledStats = useRecoilValue(feedDisabledAgentStatsFields);
  const [selectLocation, cancelSelectLocation] = useSelectLocationById();

  const [selectedLocation, setSelectedLocation] = useRecoilState(
    viewerSelectedLocationIdWithToggle,
  );

  const layoutId = useRecoilValue(viewerLayoutId);

  const resourcePolicy = useRecoilValue(actualityResourcePolicy);
  const agentMap = _.keyBy(resourcePolicy.agents, 'id');

  const { event, grouping = 'all' } = props;
  const getNamedColor = useGetNamedColor();
  const isLocationSelected = selectedLocation === event.layoutLocationId;

  const eventColors = getNamedColor(event['agentId']);
  const eventColor = eventColors?.[0];

  // Get shared categories and adapt for event data (remove 'analyzed' prefix)
  const categories = useActualityStatsCategories('h', '$'); // Use default values for event display
  const durationCategory = categories.find(c => c.key === 'duration');

  // Convert analyzed field names to raw field names for event data
  const adaptMetricName = (analyzedName: string) => {
    return analyzedName.replace('analyzed', '').replace('Duration', 'duration');
  };

  // Create metrics configuration for the stacked line chart using shared categories
  const durationMetricsConfig: StackedLineMetricConfig = {
    key: durationCategory.key,
    title: durationCategory.title,
    icon: durationCategory.icon as string,
    metrics: durationCategory.metrics.map(adaptMetricName),
    metricGroups: durationCategory.metricGroups.map(group => ({
      title: group.title,
      metrics: group.metrics.map(adaptMetricName),
    })),
  };

  // Prepare data for the stacked line chart
  const chartData = {
    duration: event.duration || 0,
    durationTravellingStart: event.durationTravellingStart || 0,
    durationTravellingEnd: event.durationTravellingEnd || 0,
    durationTravellingInternal:
      event.durationTravellingEnd - event.durationTravellingStart || 0,
    durationTravellingVertical: event.durationTravellingVertical || 0,
    durationHandling: event.durationHandling || 0,
    durationPicking: event.durationPicking || 0,
    durationReordering: event.durationReordering || 0,
  };

  let eventDurationCalcRaw: number | null = null;
  if (event.eventEndTime && event.eventStartTime) {
    eventDurationCalcRaw = Math.abs(
      event.eventEndTime.getTime() - event.eventStartTime.getTime(),
    );
  }
  const eventDurationCalc = formatter.formatTimeSpan(eventDurationCalcRaw);

  // Basic stats for the header (using configuration system for consistency)
  const stats = _.compact([
    {
      title: t`From:`,
      value: props.event.locationId,
      entityProps: {
        entity: 'location' as const,
        title: props.event.locationId,
        filters: {
          locationId: props.event.locationId,
        },
      } as ActualityEntityTagProps,
    },
    // {
    //   title: t`To:`,
    //   value: props.event.locationIdSource,
    // },
    {
      title: t`Item:`,
      value: props.event.sku,
      entityProps: {
        entity: 'item' as const,
        title: props.event.sku,
        filters: {
          sku: props.event.sku,
          consigneId: props.event.consignee,
        },
      } as ActualityEntityTagProps,
    },
  ]);

  const offset = !_.isNil(event.eventStartTime)
    ? Math.abs(event.eventEndTime.getTime() - event.eventStartTime.getTime())
    : 0;

  const issues = [];

  const selectedEvent = useRecoilValue(actualitySelectedEvent);
  const isActive = selectedEvent?.eventId === event.eventId;
  const visibleDeviceIndicators = useRecoilValue(
    actualityVisibleDeviceIndicatorsSelector,
  );

  return (
    <>
      {/* Debug component - only shows in development */}
      {/* <EventConfigDebug event={event} grouping={grouping} /> */}

      <ConfigurableEventIssues
        event={event}
        // grouping={grouping}
        timing="before"
      />
      <EventSection
        id={`feed-event-section-${event.eventId}`}
        fullWidth
        inPanelView
        collapsible
        isSelected={false}
        titleContainer={
          <>
            <EventListItemHeader
              onClick={() => {
                console.log('FeedActivityEventDetails clicked', {
                  props,
                  event: props.event,
                });
                props.onClick?.(props.event);
                if (isLocationSelected) {
                  setSelectedLocation(null);
                } else {
                  if (props.event.layoutLocationId) {
                    selectLocation({
                      layoutId,
                      locationId: props.event.layoutLocationId,
                    });
                  }
                }
              }}
              titleTag={t`Event`}
              title={
                <>
                  {_.isNil(event.layoutLocationId) && (
                    <IssueMiniInline
                      className="ltr:ml-1 rtl:mr-1"
                      key={`issue-${props.event.locationId}`}
                      title={t('Location Not Found {{locationId}}', {
                        locationId: props.event.layoutLocationId,
                      })}
                      value={event.layoutLocationId}
                    />
                  )}
                  <ButtonLocateLocation
                    disabled={_.isNil(event.layoutLocationId)}
                    locationId={props.event.locationId}
                    title={props.event.agentId}
                    icon={getAgentIcon(
                      agentMap[props.event.agentType]
                        ?.resourceTypeId as AgentCategoryId,
                    )}
                  >
                    <div
                      data-component="indicatorContainer"
                      className="flex gap-x-2"
                    >
                      {/* Device indicators with visibility management */}
                      {props.event.agentEnergy &&
                        visibleDeviceIndicators.includes('agentEnergy') && (
                          <IndicatorEnergy
                            percentage={props.event.agentEnergy}
                            isCharging={false}
                            className={classNames('h-auto w-9', 'text-menu/50')}
                          />
                        )}
                      {props.event.agentSignalStrength &&
                        visibleDeviceIndicators.includes(
                          'agentSignalStrength',
                        ) && (
                          <IndicatorSignalStrength
                            viewType="bars"
                            dBm={props.event.agentSignalStrength}
                            className={classNames('h-auto w-9', 'text-menu/50')}
                          />
                        )}
                    </div>
                  </ButtonLocateLocation>
                </>
              }
              titleAriaLabel={t`Item`}
              counter={
                <span
                  title={t(`timestamp:\n {{deviceMaxTime}}`, {
                    deviceMaxTime: props.event.eventEndTime,
                  })}
                  className="text-minimal"
                >
                  {toHumanReadableTime(props.event.eventEndTime)}
                </span>
              }
              stats={stats}
              issues={issues}
              color={eventColors}
              serviceType="actuality"
              isActive={isActive}
            />
          </>
        }
      >
        <StackedLineChart
          config={durationMetricsConfig}
          data={chartData}
          // totalKey=""
        />
        <EventProcessDetails
          index={props.index}
          event={event}
          onHover={props.onHover}
          onClick={props.onClick}
          serviceType="actuality"
        />
        {/* <PicklistEventDetails
          index={props.index}
          event={event as any}
          onHover={props.onHover}
          onClick={props.onClick}
          serviceType="actuality"
        /> */}
        {/* <ConfigurableEventIssues
          event={event}
          separateByTiming={true}
          timing="during"
        /> */}
        {/* Updated FeedActivityEventDetails with grouping support
        <FeedActivityEventDetails
          event={event}
          grouping="agentId"  // Shows agent-specific stats
          index={index}
          onClick={handleClick}
        />
         */}
        <div
          data-component="FeedActivityEventDetails"
          className={classNames(
            'relative',
            'w-full',
            'my-0.5',
            // 'py-2',
            'ltr:pl-12 ltr:pr-4 rtl:pl-4 rtl:pr-12',
            'bg-app-panel-dark',
            'overflow-hidden',
          )}
        >
          {/* <LegendItem legendSize="Full" legendType="Info" /> */}

          {/* Mini stats for UOM and Qty - always show these */}
          {/* <ContainerStatsMini>
            <StatMiniInline title={t`UOM`} value={props.event.uom} />
            <StatMiniInline title={t`Qty`} value={props.event.quantity} />
          </ContainerStatsMini> */}

          {/* Duration calculations (keep existing logic for now) */}
          {/* <StatGroup className="mt-4"> */}
          {/* {eventDurationCalcRaw && (
              <StatListItem
                title={t`Duration`}
                value={eventDurationCalc.value}
                unitOfMeasure={eventDurationCalc.unitCode}
              />
            )} */}

          {/* Configurable stats based on grouping */}

          {/* Basic usage with different groupings */}

          {/* <ConfigurableEventStats
              event={event}
              grouping={grouping}
              // onlyVisible={true}
              layout="list"
              showCategoryTitles={true}
            /> */}
          {/* </StatGroup> */}
        </div>
      </EventSection>
      {/* 
      <ConfigurableEventIssues
        event={event}
        grouping={grouping}
        timing="after"
      /> */}
    </>
  );
};

export default FeedActivityEventDetails;
